import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getListingDetail, 
  approveListing, 
  rejectListing, 
  featureListing, 
  unfeatureListing, 
  deleteListing,
  updateListingModerationStatus,
  getListingPerformance,
  updateListingSpecializedData,
  IMAGE_BASE_URL   
} from '../../services/api';
import ListingHeader from '../../components/listing-detail/ListingHeader';
import ListingImages from '../../components/listing-detail/ListingImages';
import ListingOverview from '../../components/listing-detail/ListingOverview';
import ListingStats from '../../components/listing-detail/ListingStats';
import QuickActions from '../../components/listing-detail/QuickActions';
import ModerationHistory from '../../components/listing-detail/ModerationHistory';
import PerformanceMetrics from '../../components/listing-detail/PerformanceMetrics';
import ModerationModal from '../../components/listing-detail/ModerationModal';
import RejectionModal from '../../components/listing-detail/RejectionModal';

function ListingDetail() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState(''); 
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [performanceData, setPerformanceData] = useState(null);
  const [showPerformance, setShowPerformance] = useState(false);

  useEffect(() => {
    fetchListingDetail();
  }, [listingId]);

  const fetchListingDetail = async () => {
    try {
      setLoading(true);
      const response = await getListingDetail(listingId);
      setListing(response.data);
    } catch (err) {
      setError('Failed to load listing details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      setPerformanceLoading(true);
      const response = await getListingPerformance(listingId);
      if (response.data.success) {
        setPerformanceData(response.data);
      }
    } catch (err) {
      console.error('Failed to load performance data:', err.message);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const handleListingAction = async (action, customNotes = '') => {
    let confirmationMessage = `Are you sure you want to ${action} this listing?`;
    
    if (action === 'reject' && !customNotes) {
      setShowRejectModal(true);
      return;
    }
  
    if (action === 'delete') {
      const userAdminNotes = prompt('Please provide a reason for deletion:', 'Violation of marketplace policies');
      if (userAdminNotes === null) return;
      customNotes = userAdminNotes;
    }
  
    if (!window.confirm(confirmationMessage)) {
      return;
    }
  
    try {
      setActionLoading(true);
      
      switch (action) {
        case 'approve':
          await approveListing(listingId);
          break;
        case 'reject':
          await rejectListing(listingId, customNotes);
          setShowRejectModal(false);
          setAdminNotes('');
          break;
        case 'feature':
          await featureListing(listingId);
          break;
        case 'unfeature':
          await unfeatureListing(listingId);
          break;
        case 'delete':
          await deleteListing(listingId, customNotes);
          navigate('/admin/listings');
          return;
        default:
          break;
      }
      
      await fetchListingDetail();
    } catch (err) {
      setError(`Failed to ${action} listing: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus, notes = '') => {
    if (!newStatus) return;

    let confirmationMessage = `Are you sure you want to change moderation status to "${newStatus}"?`;
    
    if (newStatus === 'rejected' && !notes) {
      setSelectedStatus(newStatus);
      setShowRejectModal(true);
      return;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setActionLoading(true);
      await updateListingModerationStatus(listingId, newStatus, notes);
      await fetchListingDetail();
      setShowStatusModal(false);
      setSelectedStatus('');
    } catch (err) {
      setError(`Failed to update moderation status: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSpecializedDataUpdate = async (specializedData) => {
    try {
      setActionLoading(true);
      await updateListingSpecializedData(listingId, specializedData);
      await fetchListingDetail();
    } catch (err) {
      setError(`Failed to update specialized data: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewPerformance = async () => {
    setShowPerformance(true);
    if (!performanceData) {
      await fetchPerformanceData();
    }
  };

  if (loading && !listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold text-white mb-2">Loading Listing Details</h3>
          <p className="text-gray-400">Fetching comprehensive listing data...</p>
        </div>
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center p-6 rounded-2xl bg-gray-800 border-2 border-red-700 max-w-md mx-4 backdrop-blur-sm">
          <div className="text-red-400 text-6xl mb-4">❌</div>
          <h3 className="text-xl font-bold text-white mb-3">Error Loading Listing</h3>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/admin/listings')}
              className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-r from-[#ff660e] to-orange-500"
            >
              ← Back to Listings
            </button>
            <button
              onClick={fetchListingDetail}
              className="px-5 py-2.5 rounded-xl font-medium border-2 transition-all duration-200 hover:shadow hover:scale-[1.02] flex items-center gap-2 group"
              style={{ 
                borderColor: '#ff660e',
                color: '#ff660e',
                backgroundColor: 'transparent'
              }}
            >
              <span className="group-hover:rotate-180 transition-transform duration-500">🔄</span>
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <ListingHeader 
        listing={listing}
        listingId={listingId}
        onNavigateBack={() => navigate('/admin/listings')}
        onShowStatusModal={() => setShowStatusModal(true)}
        onAction={handleListingAction}
        actionLoading={actionLoading}
        onViewPerformance={handleViewPerformance}
      />

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-700 bg-red-900/20 flex items-center justify-between animate-fadeIn backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-800/30">
              <span className="text-red-400">⚠️</span>
            </div>
            <p className="text-red-300 font-medium">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-800/30 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Images Section */}
          <ListingImages 
            images={listing?.images}
            firstImageUrl={listing?.first_image_url}
            categoryName={listing?.category_name}
            activeImageIndex={activeImageIndex}
            onImageChange={setActiveImageIndex}
            imageBaseUrl={IMAGE_BASE_URL}
            title={listing?.title}
          />

          {/* Listing Overview */}
          <ListingOverview 
            listing={listing}
            onSpecializedDataUpdate={handleSpecializedDataUpdate}
            actionLoading={actionLoading}
          />
        </div>

        {/* Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Statistics */}
          <ListingStats listing={listing} />

          {/* Quick Actions */}
          <QuickActions 
            listingId={listingId}
            sellerId={listing?.seller_id}
            listing={listing}
          />

          {/* Performance Metrics (Collapsible) */}
          {showPerformance && (
            <PerformanceMetrics 
              performanceData={performanceData}
              loading={performanceLoading}
              onClose={() => setShowPerformance(false)}
            />
          )}

          {/* Moderation History */}
          {listing?.moderated_at && (
            <ModerationHistory listing={listing} />
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      <ModerationModal 
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedStatus('');
        }}
        currentStatus={listing?.moderation_status}
        onStatusChange={handleStatusChange}
      />

      {/* Rejection Modal */}
      <RejectionModal 
        isOpen={showRejectModal || selectedStatus === 'rejected'}
        adminNotes={adminNotes}
        onNotesChange={setAdminNotes}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedStatus('');
          setAdminNotes('');
        }}
        onConfirm={() => {
          if (selectedStatus === 'rejected') {
            handleStatusChange('rejected', adminNotes);
          } else {
            handleListingAction('reject', adminNotes);
          }
        }}
        actionLoading={actionLoading}
      />

      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(-10px);
          }
          to { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default ListingDetail;